import { useState } from 'react'
import { useIsMomMode } from '../../hooks/useIsMomMode'
import { useAllConsigneeInventory, useLogConsigneeSale } from '../../hooks/useConsigneeInventory'
import { useProducts } from '../../hooks/useProducts'
import { useSales } from '../../hooks/useSales'
import { computeUnsettledTotals } from '../../hooks/useConsigneeSettlements'
import { Card, BottomSheet, Skeleton } from '../../components/ui'
import { useToast } from '../../components/ToastContext'
import { formatMoney, emojiForProduct, colorPairForProduct } from '../../lib/format'

export default function MomHome() {
  const { consignee, loading: detecting } = useIsMomMode()
  const { data: inventory, isLoading: loadingInventory } = useAllConsigneeInventory()
  const { data: products } = useProducts()
  const { data: sales } = useSales()
  const logSale = useLogConsigneeSale()
  const { toast } = useToast()

  const [picked, setPicked] = useState<{ productId: string; productName: string } | null>(null)
  const [salePrice, setSalePrice] = useState('')
  const [saleError, setSaleError] = useState('')

  if (detecting || loadingInventory) {
    return (
      <div style={{ padding: 'var(--space-4)' }}>
        <Skeleton height={80} style={{ marginBottom: 'var(--space-3)' }} />
        <Skeleton height={120} />
      </div>
    )
  }
  if (!consignee) {
    return (
      <div style={{ padding: 'var(--space-6)', textAlign: 'center' }}>
        <p style={{ color: 'var(--color-text-muted)' }}>
          Your access was removed. Ask the shop owner for a new invite.
        </p>
      </div>
    )
  }

  const productById = new Map((products ?? []).map((p) => [p.id, p]))
  const stockedRows = (inventory ?? []).filter((r) => r.quantity_on_hand > 0)

  const todayKey = new Date().toISOString().slice(0, 10)
  const todaysSales = (sales ?? []).filter(
    (s) => s.consignee_id === consignee.id && !s.is_refunded && s.sale_date === todayKey,
  )
  const todayTotal = todaysSales.reduce((sum, s) => sum + Number(s.gross_amount), 0)
  const totals = computeUnsettledTotals(sales ?? [], consignee.id)

  const openSale = (productId: string, productName: string, defaultPrice: number) => {
    setPicked({ productId, productName })
    setSalePrice(defaultPrice > 0 ? defaultPrice.toFixed(2) : '')
    setSaleError('')
  }

  const handleConfirm = async () => {
    setSaleError('')
    if (!picked) return
    const price = Number(salePrice)
    if (!Number.isFinite(price) || price <= 0) {
      setSaleError('Enter a price greater than $0.')
      return
    }
    try {
      await logSale.mutateAsync({
        consignee_id: consignee.id,
        product_id: picked.productId,
        sale_price: price,
      })
      toast({ tone: 'success', text: `Logged ${picked.productName} · ${formatMoney(price)}` })
      setPicked(null)
    } catch (e) {
      setSaleError((e as Error).message)
    }
  }

  return (
    <div style={{ padding: 'var(--space-4)' }}>
      <Card surface="hero" style={{ marginBottom: 'var(--space-4)' }}>
        <div style={{ fontSize: 'var(--font-size-xs)', opacity: 0.85, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Today
        </div>
        <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, marginTop: 4 }}>
          {formatMoney(todayTotal)}
        </div>
        <div style={{ fontSize: 'var(--font-size-xs)', opacity: 0.9, marginTop: 'var(--space-1)' }}>
          You owe Richard: <strong>{formatMoney(totals.ownerReceives)}</strong>
          {totals.count > 0 ? ` · ${totals.count} unsettled` : ''}
        </div>
      </Card>

      {stockedRows.length === 0 ? (
        <Card>
          <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: 'var(--space-4)' }}>
            Nothing here yet — ask the shop owner to hand off some products.
          </p>
        </Card>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 'var(--space-3)',
          }}
        >
          {stockedRows.map((row) => {
            const product = productById.get(row.product_id)
            if (!product) return null
            const [light, dark] = colorPairForProduct(product.name)
            return (
              <button
                key={row.id}
                type="button"
                onClick={() =>
                  openSale(
                    product.id,
                    product.name,
                    product.default_unit_price != null ? Number(product.default_unit_price) : 0,
                  )
                }
                style={{
                  background: `linear-gradient(135deg, ${light}, ${dark})`,
                  color: 'white',
                  border: 'none',
                  borderRadius: 'var(--radius-lg)',
                  padding: 'var(--space-4)',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                <div style={{ fontSize: '2.25rem', lineHeight: 1, marginBottom: 'var(--space-2)' }}>
                  {emojiForProduct(product.name)}
                </div>
                <div style={{ fontWeight: 700, fontSize: 'var(--font-size-sm)', marginBottom: 2 }}>
                  {product.name}
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', opacity: 0.85 }}>
                  {row.quantity_on_hand} left
                  {product.default_unit_price != null
                    ? ` · ${formatMoney(Number(product.default_unit_price))}`
                    : ''}
                </div>
              </button>
            )
          })}
        </div>
      )}

      <BottomSheet open={!!picked} onClose={() => setPicked(null)} title={picked ? `Sold one ${picked.productName}?` : ''}>
        <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-1)' }}>
          Price
        </label>
        <input
          type="text"
          inputMode="decimal"
          value={salePrice}
          onChange={(e) => setSalePrice(e.target.value)}
          placeholder="e.g., 2.00"
          autoFocus
          style={{
            width: '100%',
            padding: 'var(--space-3)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border)',
            background: 'var(--color-bg)',
            color: 'var(--color-text)',
            marginBottom: 'var(--space-3)',
            fontSize: 'var(--font-size-lg)',
          }}
        />
        {saleError && (
          <p style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-3)' }}>
            {saleError}
          </p>
        )}
        <button
          type="button"
          onClick={handleConfirm}
          disabled={logSale.isPending}
          style={{
            width: '100%',
            padding: 'var(--space-3)',
            background: 'var(--color-success)',
            color: 'white',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
            fontSize: 'var(--font-size-base)',
          }}
        >
          {logSale.isPending ? 'Logging…' : 'Confirm sale'}
        </button>
      </BottomSheet>
    </div>
  )
}
